import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import styles from "./ConfirmDialog.module.css";

/**
 * Themed confirm dialog with a Promise-based API.
 *
 *   import { confirmDialog } from "./ConfirmDialog";
 *   const ok = await confirmDialog({ title: "Delete?", danger: true });
 *
 * Renders into a single shared portal so callers don't have to wire up
 * any local state.
 */

let resolver = null;

const Portal = ({ children }) => {
  const [root] = useState(() => {
    let el = document.getElementById("confirm-portal");
    if (!el) {
      el = document.createElement("div");
      el.id = "confirm-portal";
      document.body.appendChild(el);
    }
    return el;
  });
  return ReactDOM.createPortal(children, root);
};

const ConfirmHost = () => {
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    const handler = (e) => setOpts(e.detail);
    window.addEventListener("cmbsdlt:confirm", handler);
    return () => window.removeEventListener("cmbsdlt:confirm", handler);
  }, []);

  const close = useCallback((value) => {
    if (resolver) {
      resolver(value);
      resolver = null;
    }
    setOpts(null);
  }, []);

  useEffect(() => {
    if (!opts) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opts, close]);

  if (!opts) return null;
  const { title, message, confirmLabel = "Confirm", cancelLabel = "Cancel", danger = false } = opts;

  return (
    <Portal>
      <div className={styles.overlay} role="dialog" aria-modal="true" onClick={() => close(false)}>
        <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
          <div className={`${styles.iconBadge} ${danger ? styles.iconDanger : styles.iconNeutral}`}>
            <i className={`bi ${danger ? "bi-exclamation-triangle-fill" : "bi-question-circle"}`} />
          </div>
          {title && <h3 className={styles.title}>{title}</h3>}
          {message && <p className={styles.message}>{message}</p>}
          <div className={styles.actions}>
            <button type="button" className="btn btn-ghost" onClick={() => close(false)}>
              {cancelLabel}
            </button>
            <button
              type="button"
              className="btn"
              style={danger ? { background: "var(--danger)", color: "var(--text-on-dark)" } : undefined}
              onClick={() => close(true)}
              autoFocus
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export const confirmDialog = (opts) =>
  new Promise((resolve) => {
    resolver = resolve;
    window.dispatchEvent(new CustomEvent("cmbsdlt:confirm", { detail: opts || {} }));
  });

export default ConfirmHost;
